import { ChangeDetectionStrategy, Component, OnInit, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { CompanySectorHttpService } from '@services/http/company/company-sector-http.service';
import { SharedModule } from '@shared/shared.module';
import { first } from 'rxjs/operators';

/**
 * Opção "todos os setores" (sem filtro). Não é string vazia: com valor vazio o `app-select` mostra o campo em branco
 * em vez do rótulo.
 */
const ALL_SECTORS = 'ALL';

/**
 * Filtro "Setor" da listagem de empresas (card 3642): todos os setores ou um setor da lista padronizada. Emite o id
 * do setor, ou vazio para todos.
 */
@Component({
  selector: 'app-company-sector-filter',
  imports: [SharedModule, FormsModule],
  template: `
    <app-select
      [theme]="'medium'"
      [options]="options()"
      [loading]="loading()"
      [ngModel]="selected()"
      (ngModelChange)="onChange($event)"
      [emptyFilterMessage]="'Nenhum setor com esse nome.'" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySectorFilterComponent implements OnInit {
  /** Setor escolhido (id), ou vazio para todos. */
  readonly sectorChange = output<string>();

  protected readonly options = signal<Option[]>([
    { label: 'Todos os setores', value: ALL_SECTORS },
  ]);
  protected readonly selected = signal<string>(ALL_SECTORS);
  protected readonly loading = signal(true);

  private readonly sectorHttp = inject(CompanySectorHttpService);

  ngOnInit(): void {
    this.sectorHttp
      .listOptions()
      .pipe(first())
      .subscribe({
        next: options => {
          this.options.set([{ label: 'Todos os setores', value: ALL_SECTORS }, ...options]);
          this.loading.set(false);
        },
        // Sem a lista, o filtro fica só com "Todos os setores"; a listagem continua funcionando.
        error: () => this.loading.set(false),
      });
  }

  /** Troca de setor no select. */
  protected onChange(value: string | null): void {
    const selected = value || ALL_SECTORS;
    this.selected.set(selected);
    this.sectorChange.emit(selected === ALL_SECTORS ? '' : selected);
  }
}
